import { restoredPortalPath } from './services/webSession.js'
import { useEffect, useRef, useState, lazy, Suspense } from 'react'
const AdminPortal = lazy(() => import('./AdminPortal.jsx'))
const LoginForm = lazy(() => import('./AdminPortal.jsx').then((m) => ({ default: m.LoginForm })))
const ResetPasswordPage = lazy(() => import('./ResetPasswordPage.jsx'))
const OnboardingPage = lazy(() => import('./OnboardingPage.jsx'))
const PublicQuotationPage = lazy(() => import('./PublicQuotationPage.jsx'))
import hrpocketLogo from './assets/hrpocket-logo.png'

/* ========== Icons ========== */
const FaceIcon = ({ size = 24, coral = false }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="8" r="3.4" />
    <path d="M5.5 19.5a7 7 0 0113 0" />
    <path d="M2.5 7V4.5A2.5 2.5 0 015 2h2.5M16.5 2H19A2.5 2.5 0 0121.5 4.5V7M21.5 17v2.5A2.5 2.5 0 0119 22h-2.5M7.5 22H5a2.5 2.5 0 01-2.5-2.5V17" stroke={coral ? '#BA1A1A' : 'currentColor'} />
  </svg>
)

const PinIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1116 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
)

const CheckIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
)

const CrossIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

const ArrowIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
)

const PlusIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
    <path d="M12 5v14M5 12h14" />
  </svg>
)

const ChevronDownIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9l6 6 6-6" />
  </svg>
)

const ShieldCheckIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="M9 12l2 2 4-4" />
  </svg>
)

const CalendarIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
)

const DownloadIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
)

const AlertTriangleIcon = ({ size = 18, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
)

const CheckCircleIcon = ({ size = 24, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

const DocumentTextIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
)

const ReceiptRefundIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 10h10a5 5 0 0 1 5 5v2" />
    <polyline points="7 6 3 10 7 14" />
    <path d="M21 14v-4a2 2 0 0 0-2-2h-3" />
  </svg>
)

const LogoMark = ({ size = 26 }) => (
  <img
    src={hrpocketLogo}
    alt="hrpocketPRO"
    style={{ width: size, height: size, objectFit: 'cover', borderRadius: '7px', display: 'block' }}
    onError={(e) => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = '/hrpocket-logo.png';
    }}
  />
)

/* ========== Hooks ========== */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal')
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('reveal--visible')
            io.unobserve(e.target)
          }
        })
      },
      { threshold: 0.12 }
    )
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])
}

function Counter({ end, suffix = '', duration = 1400 }) {
  const [val, setVal] = useState(0)
  const ref = useRef(null)
  const started = useRef(false)

  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true
          const start = performance.now()
          const tick = (now) => {
            const p = Math.min((now - start) / duration, 1)
            const eased = 1 - Math.pow(1 - p, 3)
            setVal(Math.round(end * eased))
            if (p < 1) requestAnimationFrame(tick)
          }
          requestAnimationFrame(tick)
        }
      },
      { threshold: 0.4 }
    )
    if (ref.current) io.observe(ref.current)
    return () => io.disconnect()
  }, [end, duration])

  return (
    <strong ref={ref}>
      {val}
      {suffix}
    </strong>
  )
}

/* ========== Navbar ========== */
function Navbar({ onOpenDemoModal, onOpenBrochureModal, onOpenLoginModal }) {
  const [open, setOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleScrollTo = (e, targetId) => {
    e.preventDefault()
    setOpen(false)
    if (targetId === 'home') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      if (window.location.pathname !== '/') {
        window.history.pushState({}, '', '/')
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
      return
    }
    const el = document.getElementById(targetId)
    if (el) el.scrollIntoView({ behavior: 'smooth' })
  }

  const links = [
    { id: 'features', label: 'Features' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'roles', label: 'Roles' },
    { id: 'pricing', label: 'Pricing' },
    { id: 'faq', label: 'FAQ' },
  ]

  return (
    <header className={`nav${scrolled ? ' nav--scrolled' : ''}`}>
      <div className="container nav__inner">
        <a href="/" onClick={(e) => handleScrollTo(e, 'home')} className="nav__logo">
          <span className="nav__logo-icon"><LogoMark /></span>
          hrpocketPRO
        </a>

        <nav className={`nav__links${open ? ' nav__links--open' : ''}`}>
          {links.map((l) => (
            <a key={l.id} href={`/#${l.id}`} className="nav__link" onClick={(e) => handleScrollTo(e, l.id)}>
              {l.label}
            </a>
          ))}
          <a
            href="/hrpocketpro-brochure.pdf"
            download="hrpocketPRO-Brochure.pdf"
            onClick={() => setOpen(false)}
            className="btn btn--sm"
            style={{ textDecoration: 'none', background: '#F1F4F6', color: '#15253F', border: '1px solid #E0E3E5', fontWeight: 800, padding: '8px 14px', borderRadius: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <DownloadIcon size={14} /> Brochure
          </a>
          <button
            onClick={() => { setOpen(false); onOpenDemoModal(); }}
            className="btn btn--sm"
            style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', color: '#FFFFFF', border: 'none', fontWeight: 800, padding: '8px 16px', borderRadius: '12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
          >
            <CalendarIcon size={16} /> Book a Demo
          </button>
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => { setOpen(false); onOpenLoginModal(); }}
            style={{ cursor: 'pointer' }}
          >
            Login →
          </button>
        </nav>

        <div className="nav__mobile-actions">
          <button
            type="button"
            className="nav__mobile-login-btn"
            onClick={() => { setOpen(false); onOpenLoginModal(); }}
            aria-label="Open Login Portal"
          >
            Login
          </button>
          <button
            className={`nav__toggle${open ? ' nav__toggle--active' : ''}`}
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </div>
      {open && (
        <div
          className="nav__backdrop"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}
    </header>
  )
}

/* ========== Hero ========== */
function Hero({ onOpenDemoModal, onOpenBrochureModal, onOpenLoginModal }) {
  useReveal()
  return (
    <section className="hero" id="home">
      <div className="hero__bg" aria-hidden="true">
        <div className="hero__glow hero__glow--1"></div>
        <div className="hero__glow hero__glow--2"></div>
        <div className="hero__grid"></div>
      </div>

      <div className="container hero__inner">
        <div className="hero__content">
          <span className="badge reveal">
            <span className="badge__dot"></span>
            AI-Powered HR Suite • Attendance • Payroll • HRMS
          </span>

          <h1 className="hero__title reveal">
            Secure Employee <span className="grad-text">Attendance</span>,<br />
            Powered by <span className="grad-text">AI</span>
          </h1>

          <p className="hero__subtitle reveal">
            AI Face recognition + GPS geofencing for <strong>100% accurate attendance</strong>.
            Eliminate proxy attendance, manual errors, and fraudulent check-ins. Payroll and HR
            management, unified in one powerful platform.
          </p>

          <div className="hero__cta reveal" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              onClick={onOpenDemoModal}
              className="btn btn--lg"
              style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', color: '#FFFFFF', border: 'none', borderRadius: '14px', fontWeight: 800, padding: '14px 26px', cursor: 'pointer', boxShadow: '0 8px 24px rgba(0,15,41,0.3)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <CalendarIcon size={20} /> Book a Demo <ArrowIcon />
            </button>
            <a
              href="/hrpocketpro-brochure.pdf"
              download="hrpocketPRO-Brochure.pdf"
              className="btn btn--ghost btn--lg"
              style={{ textDecoration: 'none', background: '#F1F4F6', color: '#15253F', border: '1.5px solid #E0E3E5', borderRadius: '14px', padding: '14px 22px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
            >
              <DownloadIcon size={18} /> Download Brochure
            </a>
            <button
              type="button"
              onClick={onOpenLoginModal}
              className="btn btn--primary btn--lg"
              style={{ borderRadius: '14px', padding: '14px 24px', cursor: 'pointer' }}
            >
              Login →
            </button>
          </div>

          <div className="hero__trust reveal">
            <div className="hero__trust-item">
              <Counter end={99} suffix="%+" />
              <span>Face Match<br />Accuracy</span>
            </div>
            <div className="hero__trust-item">
              <Counter end={100} suffix="%" />
              <span>Verified<br />Check-ins</span>
            </div>
            <div className="hero__trust-item">
              <span className="hero__trust-zero">0</span>
              <span>Proxy / Fake<br />GPS Allowed</span>
            </div>
          </div>
        </div>

        {/* Phone mockup */}
        <div className="hero__visual reveal">
          <div className="phone">
            <div className="phone__notch"></div>
            <div className="phone__screen">
              <div className="phone__header">
                <div className="phone__app">
                  <span className="phone__app-icon"><LogoMark size={18} /></span>
                  <div>
                    <p className="phone__app-name">hrpocketPRO</p>
                    <p className="phone__app-sub">Good Morning, Rahul </p>
                  </div>
                </div>
                <span className="phone__badge">Present</span>
              </div>

              <div className="phone__scan">
                <div className="phone__scan-face">
                  <FaceIcon size={64} coral />
                  <div className="phone__scan-line"></div>
                </div>
                <p className="phone__scan-status">
                  <span className="phone__scan-dot"></span> Face Verified • 99.8%
                </p>
              </div>

              <div className="phone__location">
                <PinIcon size={18} />
                <div className="phone__location-text">
                  <p>hrpocketPRO HQ — Office</p>
                  <span> 245m • In Geofence </span>
                </div>
                <span className="phone__location-check"><CheckIcon size={14} /></span>
              </div>

              <div className="phone__btn-row">
                <button className="phone__btn">Check In</button>
                <button className="phone__btn phone__btn--out">Check Out</button>
              </div>

              <div className="phone__list">
                <div className="phone__list-head">
                  <span>Today's Team</span>
                  <span className="phone__list-view">View all</span>
                </div>
                <div className="phone__row">
                  <span className="phone__avatar" style={{ '--av': 0 }}>A</span>
                  <span className="phone__row-name">Ananya S.</span>
                  <span className="phone__chip phone__chip--green">9:02 AM</span>
                </div>
                <div className="phone__row">
                  <span className="phone__avatar" style={{ '--av': 1 }}>R</span>
                  <span className="phone__row-name">Rohit M.</span>
                  <span className="phone__chip phone__chip--yellow">9:47 AM</span>
                </div>
                <div className="phone__row">
                  <span className="phone__avatar" style={{ '--av': 2 }}>P</span>
                  <span className="phone__row-name">Priya K.</span>
                  <span className="phone__chip phone__chip--red">Absent</span>
                </div>
              </div>
            </div>
          </div>

          <div className="float-card float-card--1">
            <span className="float-card__icon float-card__icon--scan"><FaceIcon size={16} /></span>
            <div>
              <p>Face Verified</p>
              <span>AI Match • 99.8%</span>
            </div>
          </div>

          <div className="float-card float-card--2">
            <span className="float-card__icon float-card__icon--pin"><PinIcon size={16} /></span>
            <div>
              <p>Location Locked</p>
              <span>Geofence In Range</span>
            </div>
          </div>

          <div className="float-card float-card--3">
            <span className="float-card__icon float-card__icon--pay">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" /></svg>
            </span>
            <div>
              <p>Payslip Ready</p>
              <span>PDF Downloaded</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ========== SectionHead ========== */
function SectionHead({ tag, title, before, after, sub }) {
  return (
    <div className="section__head reveal">
      <span className="section__tag">{tag}</span>
      <h2 className="section__title">
        {title} <span className="grad-text">{before}</span>
        {after && (
          <>
            {' '}<span className="grad-text">{after}</span>
          </>
        )}
      </h2>
      <p className="section__subtitle">{sub}</p>
    </div>
  )
}

/* ========== Problem / Solution ========== */
function ProblemSolution() {
  useReveal()

  const bad = [
    { title: 'Proxy Attendance', desc: 'Colleagues punch in on behalf of others without verification.' },
    { title: 'Manual Errors', desc: 'Spreadsheet typos, inaccurate timestamps, and payroll calculation mismatches.' },
    { title: 'Fake GPS Check-ins', desc: 'Employees spoof check-ins from home using fake GPS location apps.' },
    { title: 'Payroll Nightmare', desc: 'Monthly salary processing for hundreds of employees takes days and introduces errors.' },
  ]

  const good = [
    { title: 'AI Face Match', desc: 'Live face verification matches registered biometric vector with <b>99%+ accuracy</b>, eliminating proxies.' },
    { title: 'Haversine Geofencing', desc: 'GPS radius enforcement ensures check-ins occur strictly <b>within office boundaries</b>.' },
    { title: 'Mock Location Detection', desc: 'Identifies and blocks spoofed GPS apps instantly for tamper-proof verification.' },
    { title: 'Auto Payroll', desc: 'Computes salaries automatically from verified attendance logs with <b>zero calculation errors</b>.' },
  ]

  return (
    <section className="section" id="problem">
      <div className="container">
        <SectionHead
          tag="The Problem"
          title="Legacy Attendance Systems"
          before="Slow Down Growth"
          sub="Manual registers, punch cards, and paper-based tracking create friction. Modern enterprises require automated precision."
        />

        <div className="vs-grid">
          <div className="vs-card vs-card--bad reveal">
            <h3 className="vs-card__title">
              <span className="vs-card__icon vs-card__icon--bad">
                <CrossIcon size={18} />
              </span>
              Traditional Systems
            </h3>
            <ul className="vs-list">
              {bad.map((item) => (
                <li key={item.title}>
                  <span className="vs-list__cross"><CrossIcon size={12} /></span>
                  <div>
                    <strong>{item.title}</strong>
                    <p>{item.desc}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div className="vs-divider reveal" aria-hidden="true"><span>VS</span></div>

          <div className="vs-card vs-card--good reveal">
            <h3 className="vs-card__title">
              <span className="vs-card__icon vs-card__icon--good">
                <CheckIcon size={18} />
              </span>
              hrpocketPRO Solution
            </h3>
            <ul className="vs-list">
              {good.map((item) => (
                <li key={item.title}>
                  <span className="vs-list__check"><CheckIcon size={12} /></span>
                  <div>
                    <strong>{item.title}</strong>
                    <p dangerouslySetInnerHTML={{ __html: item.desc }} />
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ========== Admin Features ========== */
function AdminFeatures() {
  useReveal()

  const features = [
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="M7 14l4-4 3 3 5-6" /></svg>
      ),
      title: 'Real-Time Dashboard',
      desc: 'Live stats on attendance, late arrivals, pending approvals, and overtime — updated in <b>real-time</b>.',
      highlight: true,
      stats: [
        { value: '128', label: 'Present', cls: 'mini-stat--green' },
        { value: '06', label: 'Absent', cls: 'mini-stat--red' },
        { value: '04', label: 'Late', cls: 'mini-stat--yellow' },
        { value: '07', label: 'On Leave', cls: 'mini-stat--blue' },
      ],
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
      ),
      title: 'Employee Management',
      desc: 'Add, modify, or deactivate employee profiles, designated shifts, CTC structures, and hierarchy levels in seconds.',
    },
    {
      icon: <PinIcon size={22} />,
      title: 'Geofence & Branch Setup',
      desc: 'Configure <b>multiple office branches</b> with custom GPS radii, work shifts, and location-specific attendance policies.',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" /></svg>
      ),
      title: 'Reports & Exports',
      desc: 'Generate comprehensive attendance, leave, and overtime logs with <b>one-click CSV & Excel exports</b>.',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20M6 15h4" /></svg>
      ),
      title: 'Payroll & Compensation',
      desc: 'Automate monthly salary calculations directly from attendance records with automatic tax, HRA, and incentive deductions.',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15a7 7 0 100-14 7 7 0 000 14z" /><path d="M8.2 13.9L7 23l5-3 5 3-1.2-9.1" /></svg>
      ),
      title: 'PIP & Training Tracker',
      desc: 'Manage Performance Improvement Plans (PIP) and track employee learning milestones effortlessly.',
    },
    {
      icon: (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" /></svg>
      ),
      title: 'Custom Branding',
      desc: 'White-label the platform with your company <b>logo, brand colors, and custom header assets</b>.',
      wide: true,
    },
  ]

  return (
    <section className="section" id="features">
      <div className="container">
        <SectionHead
          tag="Admin & HR Features"
          title="Complete Operational"
          before="Command Center"
          sub="Designed specifically for HR leaders to streamline organization-wide workforce management."
        />
        <div className="admin-features">
          {features.map((f) => (
            <article
              key={f.title}
              className={`admin-card${f.highlight ? ' admin-card--highlight' : ''}${f.wide ? ' admin-card--wide' : ''} reveal`}
            >
              <div className="admin-card__top">
                <span className="admin-card__icon">{f.icon}</span>
                <h3 dangerouslySetInnerHTML={{ __html: f.title }} />
              </div>
              <p dangerouslySetInnerHTML={{ __html: f.desc }} />
              {f.stats && (
                <div className="mini-stats">
                  {f.stats.map((s) => (
                    <div key={s.label} className={`mini-stat ${s.cls}`}>
                      <span>{s.value}</span>
                      <small>{s.label}</small>
                    </div>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ========== How It Works ========== */
function HowItWorks() {
  useReveal()

  const steps = [
    {
      num: '01',
      icon: <FaceIcon size={26} coral />,
      title: 'Face Registration',
      desc: 'Employees complete biometric face registration in 5 seconds via a quick selfie scan.',
    },
    {
      num: '02',
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1010 10" /><path d="M12 6v6l4 2" /></svg>
      ),
      title: 'Daily Check-in / Check-out',
      desc: 'Open app, capture a live selfie with GPS verification, and check in with <b>a single tap</b>.',
    },
    {
      num: '03',
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="9" /></svg>
      ),
      title: 'AI Verification',
      desc: 'AI performs <b>face matching</b>, <b>geofence radius check</b>, and <b>mock location scan</b> instantly before confirming entry.',
    },
    {
      num: '04',
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="9" rx="1" /><rect x="14" y="3" width="7" height="5" rx="1" /><rect x="14" y="12" width="7" height="9" rx="1" /><rect x="3" y="16" width="7" height="5" rx="1" /></svg>
      ),
      title: 'Real-Time Command Center',
      desc: 'Admins track live attendance status on their command dashboard with instant report generation.',
    },
  ]

  return (
    <section className="section section--alt" id="how-it-works">
      <div className="container">
        <SectionHead
          tag="How It Works"
          title="4 Simple Steps"
          before="To Full Automation"
          sub="From onboarding to daily check-ins — frictionless setup with zero training required."
        />
        <div className="steps">
          {steps.map((s) => (
            <article key={s.num} className="step reveal">
              <span className="step__num">{s.num}</span>
              <span className="step__icon">{s.icon}</span>
              <h3>{s.title}</h3>
              <p dangerouslySetInnerHTML={{ __html: s.desc }} />
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ========== Technology ========== */
function Technology() {
  useReveal()

  const cards = [
    {
      icon: <FaceIcon size={26} />,
      title: 'Google ML Kit + MobileFaceNet',
      desc: 'Industry-standard on-device AI model executing lightning-fast face detection and verification.',
      tag: 'AI MODEL',
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2l8 4v6c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6l8-4z" /><path d="M9 12l2 2 4-4" /></svg>
      ),
      color: 'tech-card__icon--teal',
      glow: 'tech-card__glow--teal',
      tagCls: 'tech-card__tag--teal',
      title: 'Cosine Similarity Matching',
      desc: 'Biometric face vectors are matched using cosine similarity algorithm with <b>99%+ precision</b> across lighting conditions.',
      tag: '99%+',
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 018 0v4" /></svg>
      ),
      color: 'tech-card__icon--violet',
      glow: 'tech-card__glow--violet',
      tagCls: 'tech-card__tag--violet',
      title: 'Firebase-Grade Security',
      desc: 'End-to-end encryption, secure token authentication, and enterprise cloud infrastructure protecting user data.',
      tag: 'ENCRYPTED',
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 1116 0z" /><circle cx="12" cy="10" r="3" /><path d="M12 1.5v2M12 16.5v2" /></svg>
      ),
      color: 'tech-card__icon--yellow',
      glow: 'tech-card__glow--yellow',
      tagCls: 'tech-card__tag--yellow',
      title: 'Mock GPS Detection',
      desc: 'Detects and <b>instantly blocks</b> GPS spoofing applications, VPN masks, and rooted devices.',
      tag: 'BLOCKED',
    },
  ]

  return (
    <section className="section" id="technology">
      <div className="container">
        <SectionHead
          tag="Technology & Security"
          title="Under The Hood —"
          before="Enterprise Security"
          sub="Advanced AI models, encryption protocols, and cloud infrastructure engineered for maximum reliability."
        />
        <div className="tech-grid">
          {cards.map((c) => (
            <article key={c.title} className="tech-card reveal">
              <div className={`tech-card__glow${c.glow ? ' ' + c.glow : ''}`}></div>
              <span className={`tech-card__icon${c.color ? ' ' + c.color : ''}`}>{c.icon}</span>
              <h3>{c.title}</h3>
              <p dangerouslySetInnerHTML={{ __html: c.desc }} />
              <span className={`tech-card__tag${c.tagCls ? ' ' + c.tagCls : ''}`}>{c.tag}</span>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ========== Roles ========== */
function Roles() {
  useReveal()

  const roles = [
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      badgeCls: 'role-badge--employee',
      cls: 'role-card--employee',
      title: 'Employee',
      desc: 'Clock in/out, view payslips, request leaves, and manage HR documents from a unified self-service view.',
      access: 'Attendance • Leaves • Payslips'
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
      badgeCls: 'role-badge--manager',
      cls: 'role-card--manager',
      title: 'Manager',
      desc: 'Real-time team attendance oversight, leave approval workflows, and regularization request handling.',
      access: 'Team View • Approvals'
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      ),
      badgeCls: 'role-badge--admin',
      cls: 'role-card--admin',
      title: 'HR Admin',
      desc: 'Central control for employee directory, geofences, automated payroll, analytics, and policy enforcement.',
      access: 'Full Admin Control'
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
          <path d="M7 11V7a5 5 0 0 1 10 0v4" />
        </svg>
      ),
      badgeCls: 'role-badge--security',
      cls: 'role-card--security',
      title: 'Security / Gatekeeper',
      desc: 'Gate entry verification dashboard for physical security checkpoints and visitor tracking.',
      access: 'Entry Monitor • Alerts'
    },
    {
      icon: (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
        </svg>
      ),
      badgeCls: 'role-badge--super',
      cls: 'role-card--super',
      title: 'SuperAdmin',
      desc: 'Multi-company and multi-branch management console with executive-level reporting.',
      access: 'Multi-Branch • Global Reports'
    },
  ]

  return (
    <section className="section section--alt" id="roles">
      <div className="container">
        <SectionHead
          tag="User Roles"
          title="Tailored Dashboards"
          before="For Every Role"
          sub="Role-based access control ensuring every team member sees exactly what they need."
        />
        <div className="roles-grid">
          {roles.map((r) => (
            <article key={r.title} className={`role-card ${r.cls} reveal`}>
              <div className={`role-card__icon-wrap ${r.badgeCls}`}>
                {r.icon}
              </div>
              <h3>{r.title}</h3>
              <p>{r.desc}</p>
              <span className="role-card__access">{r.access}</span>
            </article>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ========== Pricing ========== */
const DEFAULT_WEBSITE_PLANS = [
  {
    name: 'Basic',
    desc: 'Designed for small growing teams',
    currency: '₹',
    amount: '29',
    per: '/employee/mo',
    features: ['AI Face Attendance', 'GPS Geofencing (1 branch)', 'Leave Management', 'Digital Payslips', 'Email Support'],
    cta: 'Book a Demo',
    outline: true,
  },
  {
    name: 'Pro',
    desc: 'Ideal for scaling businesses',
    currency: '₹',
    amount: '59',
    per: '/employee/mo',
    features: ['Everything in Basic', 'Multi-Branch Geofencing', 'Payroll Automation', 'Reports & CSV/Excel Export', 'PIP & Training Tracker', 'Priority Support'],
    cta: 'Book a Demo',
    popular: true,
  },
  {
    name: 'Enterprise',
    desc: 'Tailored for large organizations',
    currency: '₹',
    amount: 'Custom',
    per: 'pricing',
    features: ['Everything in Pro', 'Custom Branding', 'Multi-Company Support', 'Dedicated Account Manager', 'SLA & On-premise Option'],
    cta: 'Book a Demo',
    outline: true,
  },
]

function Pricing({ onOpenDemoModal, onOpenLoginModal }) {
  useReveal()
  const [plans, setPlans] = useState(DEFAULT_WEBSITE_PLANS)

  useEffect(() => {
    let isMounted = true
    fetch('/api/plans')
      .then((res) => res.json())
      .then((data) => {
        if (!isMounted) return
        if (data && data.success && Array.isArray(data.data) && data.data.length > 0) {
          const visible = data.data.filter((p) => p.showOnWebsite !== false && p.isActive !== false)
          if (visible.length > 0) {
            setPlans(visible)
          }
        }
      })
      .catch((err) => {
        console.warn('Using default website pricing plans:', err)
      })
    return () => {
      isMounted = false
    }
  }, [])

  return (
    <section className="section" id="pricing">
      <div className="container">
        <SectionHead
          tag="Pricing"
          title="Simple & Transparent"
          before="Enterprise Pricing"
          sub="Transparent plans designed for growing startups, scaling mid-size businesses, and large enterprises."
        />
        <div className="pricing-grid">
          {plans.map((p) => (
            <article key={p.name || p.planName} className={`price-card${p.popular ? ' price-card--popular' : ''} reveal`}>
              {p.popular && <span className="price-card__badge"> Most Popular</span>}
              <h3 className="price-card__name">{p.name || p.planName}</h3>
              <p className="price-card__desc">{p.desc || p.description}</p>
              <div className="price-card__amount">
                <span className="price-card__currency">{p.currency || '₹'}</span>
                <span className="price-card__num">{p.amount !== undefined ? p.amount : p.price}</span>
                <span className="price-card__per">{p.per || '/employee/mo'}</span>
              </div>
              <ul className="price-card__list">
                {(p.features || []).map((f) => (
                  <li key={f}> {f}</li>
                ))}
              </ul>
              <button
                type="button"
                onClick={onOpenDemoModal}
                className={`btn btn--block${p.outline ? ' btn--outline' : ' btn--primary'}`}
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
              >
                <CalendarIcon size={16} />
                {(!p.cta || p.cta.toLowerCase().includes('login')) ? 'Book a Demo' : p.cta}
              </button>
            </article>
          ))}
        </div>
        <p className="pricing-note reveal">
          Ready to experience hrpocketPRO? Click <strong>Book a Demo</strong> to schedule a live personalized walkthrough, or{' '}
          <span
            onClick={onOpenLoginModal}
            style={{ color: '#15253F', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
          >
            Login
          </span>{' '}
          if you already have company credentials.
        </p>
      </div>
    </section>
  )
}

/* ========== Testimonials ========== */
function Testimonials() {
  useReveal()

  const items = [
    {
      quote: '"Proxy attendance has been completely eliminated. Verified biometrics saved us lakhs in unearned salary leaks every month."',
      initials: 'AK',
      name: 'Anil Khanna',
      role: 'Founder, Khanna Textiles',
    },
    {
      quote: '"Setting up geofencing across our 3 regional branches took less than 10 minutes. I now have complete real-time visibility."',
      initials: 'SR',
      name: 'Sneha Rao',
      role: 'HR Head, UrbanNest Retail',
    },
    {
      quote: '"Monthly payroll processing that used to take 4 days is now completed in under an hour with zero calculation errors."',
      initials: 'VM',
      name: 'Vikram Mehta',
      role: 'MD, SwiftLogistics',
    },
  ]

  return (
    <section className="section section--alt" id="testimonials">
      <div className="container">
        <SectionHead
          tag="Testimonials"
          title="Trusted By"
          before="Industry Leaders"
          sub="Discover how top companies streamline attendance and payroll with hrpocketPRO:"
        />
        <div className="testi-grid">
          {items.map((t) => (
            <figure key={t.name} className="testi-card reveal">
              <div className="testi-card__stars"></div>
              <blockquote>{t.quote}</blockquote>
              <figcaption>
                <span className="testi-card__avatar">{t.initials}</span>
                <div>
                  <strong>{t.name}</strong>
                  <span>{t.role}</span>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ========== FAQ ========== */
function FAQ() {
  useReveal()
  const [openIdx, setOpenIdx] = useState(null)

  const faqs = [
    {
      q: 'Is offline attendance supported during network outages?',
      a: 'Yes! Employees can clock in or out even with spotty connectivity. As soon as network connectivity is restored, attendance logs synchronize seamlessly with cloud servers without missing any entries.',
    },
    {
      q: 'How secure is employee biometric and profile data?',
      a: '100% secure. Raw face photographs are never stored on our servers. Biometrics are encoded into 192-dimensional encrypted vectors with Firebase-grade cloud security, encrypted data transfer, and biometric app locks.',
    },
    {
      q: 'Can we configure multiple branch locations and geofences?',
      a: 'Absolutely. You can define custom GPS radii, shift schedules, and branch policies for each office location, managed centrally via the SuperAdmin and HR Admin Portals.',
    },
    {
      q: 'Does the app block fake location spoofing tools?',
      a: 'Yes. Built-in mock location detection identifies GPS spoofing apps, VPN masks, and rooted devices, blocking fraudulent check-ins immediately.',
    },
    {
      q: 'Is data migration support provided for existing records?',
      a: 'Yes, we offer complimentary data migration support to import existing employee records and attendance history without operational downtime.',
    },
  ]

  return (
    <section className="section" id="faq">
      <div className="container container--narrow">
        <SectionHead
          tag="FAQ"
          title="Frequently Asked"
          before="Questions"
          sub="Everything you need to know about hrpocketPRO implementation and features:"
        />
        <div className="faq-list reveal">
          {faqs.map((f, i) => (
            <div key={f.q} className={`faq-item${openIdx === i ? ' faq-item--open' : ''}`}>
              <button
                className="faq-item__q"
                aria-expanded={openIdx === i}
                onClick={() => setOpenIdx(openIdx === i ? null : i)}
              >
                <span>{f.q}</span>
                <span className="faq-item__icon"><PlusIcon /></span>
              </button>
              <div className="faq-item__a">
                <p>{f.a}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

/* ========== CTA / Login ========== */
function LoginCTA({ onOpenDemoModal, onOpenLoginModal }) {
  useReveal()

  return (
    <section className="cta" id="login">
      <div className="cta__bg" aria-hidden="true"><div className="cta__glow"></div></div>
      <div className="container container--narrow">
        <div className="cta__inner reveal">
          <span className="cta__badge"> Company & Super Admin</span>
          <h2 className="cta__title">Access Your <span className="grad-text">HR Dashboard</span></h2>
          <p className="cta__subtitle">Unified portal access for Company Owners, HR Admins, and SuperAdmins. Schedule a live product walkthrough, or log in with your credentials.</p>

          <div style={{ display: 'flex', gap: '14px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '20px' }}>
            <button
              type="button"
              onClick={onOpenDemoModal}
              className="btn btn--lg"
              style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', color: '#FFFFFF', border: 'none', borderRadius: '14px', fontWeight: 800, padding: '14px 26px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', boxShadow: '0 8px 24px rgba(0,15,41,0.3)' }}
            >
              <CalendarIcon size={18} /> Book a Demo <ArrowIcon />
            </button>
            <button
              type="button"
              onClick={onOpenLoginModal}
              className="btn btn--primary btn--lg"
              style={{ borderRadius: '14px', padding: '14px 26px', cursor: 'pointer' }}
            >
              Login to Portal →
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

/* ========== Footer ========== */
function Footer({ onOpenBrochureModal, onOpenLoginModal }) {
  const handleGoHome = (e) => {
    e.preventDefault()
    if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/')
      window.dispatchEvent(new PopStateEvent('popstate'))
    }
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleScrollTo = (e, targetId) => {
    e.preventDefault()
    const el = document.getElementById(targetId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    } else if (window.location.pathname !== '/') {
      window.history.pushState({}, '', '/')
      window.dispatchEvent(new PopStateEvent('popstate'))
      setTimeout(() => {
        const delayedEl = document.getElementById(targetId)
        if (delayedEl) delayedEl.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }

  const handleGoLegal = (e, path) => {
    e.preventDefault()
    window.history.pushState({}, '', path)
    window.dispatchEvent(new PopStateEvent('popstate'))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <footer className="footer">
      <div className="container">
        <div className="footer__grid">
          <div className="footer__brand">
            <a href="/" onClick={handleGoHome} className="nav__logo footer__logo">
              <span className="nav__logo-icon"><LogoMark size={24} /></span>
              Hr<span className="nav__logo-accent">PocketPRO</span>
            </a>
            <p>AI-powered Attendance, Payroll, and HR Management unified in one enterprise mobile platform. Secure, automated, and reliable.</p>
            <div className="footer__badges">
              <a
                href="https://play.google.com/store/apps/details?id=com.hrpocket.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="store-badge"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" /></svg>
                <span><small>Download on the</small><strong>App Store</strong></span>
              </a>
              <a
                href="https://play.google.com/store/apps/details?id=com.hrpocket.pro"
                target="_blank"
                rel="noopener noreferrer"
                className="store-badge"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M3.6 1.8L13.7 12 3.6 22.2c-.4-.2-.6-.6-.6-1.1V2.9c0-.5.2-.9.6-1.1zm11.8 11.8l3.1 3.1-11.6 6.4 8.5-9.5zm4.3-3.2a1.8 1.8 0 010 3.2l-3.3 1.8-3.1-3.1 3.1-3.1 3.3 1.2zM6.9 1.9l11.6 6.4-3.1 3.1-8.5-9.5z" /></svg>
                <span><small>Get it on</small><strong>Google Play</strong></span>
              </a>
            </div>
          </div>

          <div className="footer__col">
            <h4>Product</h4>
            <a href="/#features" onClick={(e) => handleScrollTo(e, 'features')}>Platform Features</a>
            <a href="/#how-it-works" onClick={(e) => handleScrollTo(e, 'how-it-works')}>How It Works</a>
            <a href="/#technology" onClick={(e) => handleScrollTo(e, 'technology')}>Technology</a>
            <a href="/#pricing" onClick={(e) => handleScrollTo(e, 'pricing')}>Pricing</a>
            <a href="/hrpocketpro-brochure.pdf" download="hrpocketPRO-Brochure.pdf" style={{ fontWeight: 800, color: '#C5D8FD', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}><DownloadIcon size={14} /> Product Brochure (PDF)</a>
          </div>

          <div className="footer__col">
            <h4>Company</h4>
            <a href="/#roles" onClick={(e) => handleScrollTo(e, 'roles')}>User Roles</a>
            <a href="/#testimonials" onClick={(e) => handleScrollTo(e, 'testimonials')}>Testimonials</a>
            <a href="/#faq" onClick={(e) => handleScrollTo(e, 'faq')}>FAQ</a>
            <a href="/login" onClick={(e) => { e.preventDefault(); if (onOpenLoginModal) onOpenLoginModal(); else { window.history.pushState({}, '', '/login'); window.dispatchEvent(new PopStateEvent('popstate')); } }}>Login</a>
            <a href="mailto:smarthomes467@gmail.com">Contact Us</a>
          </div>

          <div className="footer__col">
            <h4>Corporate Office &amp; Contacts</h4>
            <p style={{ fontSize: '0.82rem', color: '#6B7280', margin: '0 0 10px', lineHeight: 1.5 }}>
              <strong style={{ color: '#F7FAFC', display: 'block', marginBottom: '4px', fontSize: '0.86rem' }}>Bharat Expert Gen Technologies Pvt. Ltd</strong>
              1/93, Vijay Khand, Ujariyaon, Vijay Khand 2, Gomti Nagar, Lucknow, UP 226010
            </p>
            <a href="tel:+917380325516" style={{ fontWeight: 700 }}> +91 7380325516</a>
            <a href="mailto:smarthomes467@gmail.com" style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}> smarthomes467@gmail.com</a>
            <div className="footer__socials" style={{ marginTop: '14px' }}>
              <a href="/" onClick={(e) => e.preventDefault()} aria-label="LinkedIn"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4V24h-4V8zm7.5 0h3.8v2.2h.05c.53-1 1.83-2.2 3.77-2.2 4.03 0 4.78 2.65 4.78 6.1V24h-4v-8.6c0-2.05-.04-4.7-2.86-4.7-2.86 0-3.3 2.24-3.3 4.55V24H8V8z" /></svg></a>
              <a href="/" onClick={(e) => e.preventDefault()} aria-label="Twitter / X"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.41l-5.8-7.58-6.63 7.58H.47l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z" /></svg></a>
              <a href="/" onClick={(e) => e.preventDefault()} aria-label="Instagram"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="2" y="2" width="20" height="20" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg></a>
              <a href="/" onClick={(e) => e.preventDefault()} aria-label="YouTube"><svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.5 6.19a3.02 3.02 0 00-2.12-2.14C19.5 3.55 12 3.55 12 3.55s-7.5 0-9.38.5A3.02 3.02 0 00.5 6.19C0 8.07 0 12 0 12s0 3.93.5 5.81a3.02 3.02 0 002.12 2.14c1.88.5 9.38.5 9.38.5s7.5 0 9.38-.5a3.02 3.02 0 002.12-2.14C24 15.93 24 12 24 12s0-3.93-.5-5.81zM9.55 15.57V8.43L15.82 12l-6.27 3.57z" /></svg></a>
            </div>
          </div>
        </div>

        <div className="footer__bottom">
          <p>© {new Date().getFullYear()} hrpocketPRO. All rights reserved.</p>
          <div className="footer__legal">
            <a href="/privacy" onClick={(e) => handleGoLegal(e, '/privacy')}>Privacy Policy</a>
            <a href="/terms" onClick={(e) => handleGoLegal(e, '/terms')}>Terms &amp; Conditions</a>
            <a href="/refund" onClick={(e) => handleGoLegal(e, '/refund')}>Refund &amp; Cancellation Policy</a>
          </div>
        </div>
      </div>
    </footer>
  )
}

/* ========== Back to top ========== */
function BackToTop() {
  const [show, setShow] = useState(false)

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 500)
    window.addEventListener('scroll', onScroll)
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  return (
    <button
      className={`to-top${show ? ' to-top--visible' : ''}`}
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      aria-label="Back to top"
    >
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 15l-6-6-6 6" />
      </svg>
    </button>
  )
}

/* ========== Floating Call & WhatsApp Buttons ========== */
function FloatingContactButtons() {
  const whatsappNumber = '917380325516'
  const phoneNumber = '+917380325516'
  const message = encodeURIComponent('Hi hrpocketPRO Team, I would like to know more about your HR & Attendance management solution.')
  const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${message}`

  return (
    <div className="floating-contacts">
      {/* WhatsApp Button */}
      <a
        href={whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="floating-btn floating-btn--whatsapp"
        aria-label="Chat on WhatsApp"
        title="Chat with us on WhatsApp"
      >
        <span className="floating-btn__tooltip">Chat on WhatsApp</span>
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
          <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
        </svg>
      </a>

      {/* Call Button */}
      <a
        href={`tel:${phoneNumber}`}
        className="floating-btn floating-btn--call"
        aria-label="Call Us Directly"
        title="Call Us Directly"
      >
        <span className="floating-btn__tooltip">Call +91 7380325516</span>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      </a>
    </div>
  )
}

/* ========== Clean Browser History Routing ========== */
function useBrowserRoute() {
  const [route, setRoute] = useState(() => {
    let cleanPath = window.location.pathname || '/'
    const hash = window.location.hash || ''
    if (hash) {
      let queryStr = window.location.search || ''
      if (!queryStr && hash.includes('?')) {
        queryStr = '?' + hash.substring(hash.indexOf('?') + 1)
      }
      if (hash === '#admin' || hash === '#login') {
        cleanPath = '/admin'
      } else if (hash.startsWith('#/')) {
        const raw = hash.slice(2).split('?')[0]
        const legacyMap = { staff: 'employees', exited_staff: 'exited-employees', org: 'organization', leave: 'organization', leaves: 'organization', doc_studio: 'document-templates' }
        cleanPath = `/${legacyMap[raw] || raw}`
      } else if (hash === '#privacy') cleanPath = '/privacy'
      else if (hash === '#terms') cleanPath = '/terms'
      else if (hash === '#refund') cleanPath = '/refund'
      else if (hash.startsWith('#reset-password') || hash.startsWith('#/reset-password')) cleanPath = '/reset-password'
      else if (hash === '#home' || hash === '#') cleanPath = '/'
      window.history.replaceState({}, '', `${cleanPath}${queryStr}`)
    }
    const restoredPath = restoredPortalPath(cleanPath, window.location.search, hash)
    if (restoredPath !== cleanPath) {
      cleanPath = restoredPath
      window.history.replaceState({}, '', cleanPath)
    }
    return { pathname: cleanPath }
  })

  useEffect(() => {
    const handleRouteChange = () => {
      let cleanPath = window.location.pathname || '/'
      const hash = window.location.hash || ''
      if (hash) {
        let queryStr = window.location.search || ''
        if (!queryStr && hash.includes('?')) {
          queryStr = '?' + hash.substring(hash.indexOf('?') + 1)
        }
        if (hash === '#admin' || hash === '#login') {
          cleanPath = '/admin'
        } else if (hash.startsWith('#/')) {
          const raw = hash.slice(2).split('?')[0]
          const legacyMap = { staff: 'employees', exited_staff: 'exited-employees', org: 'organization', leave: 'organization', leaves: 'organization', doc_studio: 'document-templates' }
          cleanPath = `/${legacyMap[raw] || raw}`
        } else if (hash === '#privacy') cleanPath = '/privacy'
        else if (hash === '#terms') cleanPath = '/terms'
        else if (hash === '#refund') cleanPath = '/refund'
        else if (hash.startsWith('#reset-password') || hash.startsWith('#/reset-password')) cleanPath = '/reset-password'
        else if (hash === '#home' || hash === '#') cleanPath = '/'
        window.history.replaceState({}, '', `${cleanPath}${queryStr}`)
      }
      setRoute({ pathname: cleanPath })
    }

    handleRouteChange()
    window.addEventListener('popstate', handleRouteChange)
    window.addEventListener('hashchange', handleRouteChange)
    return () => {
      window.removeEventListener('popstate', handleRouteChange)
      window.removeEventListener('hashchange', handleRouteChange)
    }
  }, [])

  return route
}

/* ========== Login Modal (Popup) ========== */
function LoginModal({ isOpen, onClose, onLoginSuccess }) {
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div
      className="login-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="login-modal-title"
    >
      <div className="login-modal-card">
        <button
          type="button"
          className="login-modal-close"
          onClick={onClose}
          aria-label="Close login dialog"
        >
          &times;
        </button>

        <div className="login-modal-body">
          <Suspense fallback={<div style={{ textAlign: 'center', padding: '36px 0', color: '#64748B', fontSize: '0.88rem' }}>Loading authentication portal...</div>}>
            <LoginForm onLogin={onLoginSuccess} isModal={true} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}

/* ========== App ========== */
export default function App() {
  const { pathname } = useBrowserRoute()
  const onboardingInviteId = new URLSearchParams(window.location.search).get('onboarding')
  const [showDemoModal, setShowDemoModal] = useState(false)
  const [showBrochureModal, setShowBrochureModal] = useState(false)
  const [showLoginModal, setShowLoginModal] = useState(false)

  const handleLoginSuccess = ({ token, user }) => {
    setShowLoginModal(false)
    const role = String(user?.role || '').toLowerCase()
    const targetPath = (role === 'employee' || role === 'superadmin') ? '/admin' : '/dashboard'
    window.history.pushState({}, '', targetPath)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }

  const pageLoader = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', width: '100%', background: '#F8FAFC' }}>
      <div style={{ width: '36px', height: '36px', border: '3.5px solid rgba(21, 37, 63, 0.1)', borderTopColor: '#15253F', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )

  if (onboardingInviteId) {
    return (
      <Suspense fallback={pageLoader}>
        <OnboardingPage inviteId={onboardingInviteId} />
      </Suspense>
    )
  }

  // Direct Brochure Download Route
  if (pathname === '/brochure' || pathname === '/brochure.pdf') {
    window.location.assign('/hrpocketpro-brochure.pdf');
    return null;
  }

  // Password reset route (Clean URL)
  if (pathname.startsWith('/reset-password')) {
    const searchParams = new URLSearchParams(window.location.search);
    let resetToken = searchParams.get('token') || '';
    if (!resetToken && window.location.hash.includes('?')) {
      const hashParams = new URLSearchParams(window.location.hash.substring(window.location.hash.indexOf('?')));
      resetToken = hashParams.get('token') || '';
    }
    if (!resetToken) {
      const match = window.location.href.match(/[?&]token=([^&#]+)/);
      if (match) resetToken = decodeURIComponent(match[1]).trim();
    }

    return (
      <Suspense fallback={pageLoader}>
        <ResetPasswordPage
          token={resetToken}
          onGoLogin={() => {
            window.history.pushState({}, '', '/');
            window.dispatchEvent(new PopStateEvent('popstate'));
          }}
        />
      </Suspense>
    );
  }

  // Public quotation route. It must stay outside every admin/auth guard.
  const publicQuotationMatch = pathname.match(/^\/q\/([^/]+)\/?$/)
  if (publicQuotationMatch) {
    return (
      <Suspense fallback={pageLoader}>
        <PublicQuotationPage token={decodeURIComponent(publicQuotationMatch[1])} />
      </Suspense>
    )
  }

  const adminRoutes = new Set([
    'admin', 'login', 'dashboard', 'employees', 'exited-employees', 'invite', 'attendance', 'leaves', 'leave',
    'grievance', 'organization', 'payroll', 'lifecycle', 'hub', 'quotations', 'ledger',
    'geofence', 'reports', 'taxes', 'loans', 'salary', 'pip', 'document-templates', 'settings',
  ])
  const routeRoot = pathname.split('/').filter(Boolean)[0] || ''
  if (adminRoutes.has(routeRoot)) {
    return (
      <Suspense fallback={pageLoader}>
        <AdminPortal onGoHome={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')) }} />
      </Suspense>
    )
  }

  // Standalone Full-Page Legal Policy Routes (Clean URLs)
  if (pathname === '/privacy' || pathname.startsWith('/privacy')) {
    return <PrivacyPolicyPage onGoHome={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')) }} />
  }
  if (pathname === '/terms' || pathname.startsWith('/terms')) {
    return <TermsConditionsPage onGoHome={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')) }} />
  }
  if (pathname === '/refund' || pathname.startsWith('/refund')) {
    return <RefundPolicyPage onGoHome={() => { window.history.pushState({}, '', '/'); window.dispatchEvent(new PopStateEvent('popstate')) }} />
  }

  return (
    <>
      <Navbar
        onOpenDemoModal={() => setShowDemoModal(true)}
        onOpenBrochureModal={() => setShowBrochureModal(true)}
        onOpenLoginModal={() => setShowLoginModal(true)}
      />
      <main>
        <Hero
          onOpenDemoModal={() => setShowDemoModal(true)}
          onOpenBrochureModal={() => setShowBrochureModal(true)}
          onOpenLoginModal={() => setShowLoginModal(true)}
        />
        <ProblemSolution />
        <AdminFeatures />
        <HowItWorks />
        <Technology />
        <Roles />
        <Pricing
          onOpenDemoModal={() => setShowDemoModal(true)}
          onOpenLoginModal={() => setShowLoginModal(true)}
        />
        <Testimonials />
        <FAQ />
        <LoginCTA
          onOpenDemoModal={() => setShowDemoModal(true)}
          onOpenLoginModal={() => setShowLoginModal(true)}
        />
      </main>
      <Footer
        onOpenBrochureModal={() => setShowBrochureModal(true)}
        onOpenLoginModal={() => setShowLoginModal(true)}
      />
      <BackToTop />
      <FloatingContactButtons />

      {showDemoModal && (
        <BookDemoModal onClose={() => setShowDemoModal(false)} />
      )}

      {showBrochureModal && (
        <BrochureModal onClose={() => setShowBrochureModal(false)} />
      )}

      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  )
}

/* ========== Product Brochure Modal Component ========== */
function BrochureModal({ onClose }) {
  const handleDownloadPDF = () => {
    window.print()
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.75)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justify: 'center',
        padding: '20px',
        overflowY: 'auto',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#FFFFFF',
          borderRadius: '28px',
          maxWidth: '850px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
          border: '1px solid #E5E9EB',
          position: 'relative',
          padding: '0',
        }}
        onClick={(e) => e.stopPropagation()}
        className="brochure-printable"
      >
        {/* Modal Top Actions (Non-printable header) */}
        <div
          className="no-print"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            padding: '16px 24px',
            borderBottom: '1px solid #E5E9EB',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTopLeftRadius: '28px',
            borderTopRightRadius: '28px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.2rem' }}></span>
            <strong style={{ fontSize: '1.05rem', color: '#15253F', fontWeight: 900 }}>hrpocketPRO Enterprise Product Brochure</strong>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <a
              href="/hrpocketpro-brochure.pdf"
              download="hrpocketPRO-Brochure.pdf"
              style={{
                textDecoration: 'none',
                background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)',
                color: '#FFFFFF',
                border: 'none',
                padding: '10px 18px',
                borderRadius: '12px',
                fontWeight: 800,
                fontSize: '0.88rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 4px 14px rgba(0, 15, 41, 0.25)',
              }}
            >
              <DownloadIcon size={16} /> Download Official PDF
            </a>
            <button
              onClick={handleDownloadPDF}
              style={{
                background: '#F1F4F6',
                color: '#15253F',
                border: '1px solid #CBD5E1',
                padding: '10px 16px',
                borderRadius: '12px',
                fontWeight: 700,
                fontSize: '0.88rem',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              Print
            </button>
            <button
              onClick={onClose}
              style={{
                background: '#F1F4F6',
                color: '#44474D',
                border: 'none',
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                fontWeight: 900,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1rem',
              }}
            >
              <CrossIcon size={18} />
            </button>
          </div>
        </div>

        {/* Brochure Printable Body */}
        <div style={{ padding: '36px 40px', color: '#181C1E', lineHeight: 1.6 }}>
          {/* Header Banner */}
          <div style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', borderRadius: '20px', padding: '32px', color: '#FFFFFF', marginBottom: '32px', boxShadow: '0 12px 30px rgba(0, 15, 41, 0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <LogoMark size={36} />
                  <span style={{ fontSize: '1.8rem', fontWeight: 900, letterSpacing: '-0.5px' }}>Hr<span style={{ color: '#C5D8FD' }}>PocketPRO</span></span>
                </div>
                <h1 style={{ margin: 0, fontSize: '1.7rem', fontWeight: 900, color: '#FFFFFF' }}>Enterprise HRMS &amp; Biometric Attendance Platform</h1>
                <p style={{ margin: '8px 0 0', fontSize: '0.95rem', color: '#C5D8FD', fontWeight: 600 }}>Product Overview &amp; Specifications • Official Brochure 2026</p>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', padding: '12px 20px', borderRadius: '14px', textAlign: 'center' }}>
                <span style={{ display: 'block', fontSize: '0.72rem', fontWeight: 800, color: '#C5D8FD', textTransform: 'uppercase', letterSpacing: '1px' }}>AI Match Accuracy</span>
                <strong style={{ fontSize: '1.6rem', fontWeight: 900, color: '#FFFFFF' }}>99.8%</strong>
              </div>
            </div>
          </div>

          {/* Key Value Proposition */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#15253F', fontSize: '1.25rem', fontWeight: 900, marginBottom: '12px', borderBottom: '2px solid #EBEEF0', paddingBottom: '8px' }}>
              Why Enterprises Choose hrpocketPRO
            </h2>
            <p style={{ margin: 0, fontSize: '0.94rem', color: '#44474D', lineHeight: 1.7 }}>
              hrpocketPRO is India's premier AI-driven workforce management suite designed to eliminate proxy attendance, streamline payroll computation, and ensure 100% geofence compliance across multiple office branches and remote field teams.
            </p>
          </div>

          {/* Core Feature Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '32px' }}>
            <div style={{ background: '#F1F4F6', border: '1.5px solid #E0E3E5', borderRadius: '18px', padding: '22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}></span>
                <h3 style={{ margin: 0, color: '#15253F', fontSize: '1.08rem', fontWeight: 900 }}>AI Face Recognition Attendance</h3>
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.88rem', color: '#44474D', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li>Live selfie verification under 1 second.</li>
                <li>192-dimensional non-reversible vector encryption.</li>
                <li>Zero raw photo storage for 100% GDPR compliance.</li>
              </ul>
            </div>

            <div style={{ background: '#F1F4F6', border: '1.5px solid #E0E3E5', borderRadius: '18px', padding: '22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}></span>
                <h3 style={{ margin: 0, color: '#15253F', fontSize: '1.08rem', fontWeight: 900 }}>Haversine GPS Geofencing</h3>
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.88rem', color: '#44474D', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li>Strict GPS radius check-in validation.</li>
                <li>Real-time mock GPS &amp; location spoofing detection.</li>
                <li>Multi-branch &amp; dynamic remote site support.</li>
              </ul>
            </div>

            <div style={{ background: '#F1F4F6', border: '1.5px solid #E0E3E5', borderRadius: '18px', padding: '22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}></span>
                <h3 style={{ margin: 0, color: '#15253F', fontSize: '1.08rem', fontWeight: 900 }}>Automated Statutory Payroll</h3>
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.88rem', color: '#44474D', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li>1-click monthly salary generation.</li>
                <li>Auto PF, ESI, TDS, and LWF deductions.</li>
                <li>Instant PDF payslip generation &amp; distribution.</li>
              </ul>
            </div>

            <div style={{ background: '#F1F4F6', border: '1.5px solid #E0E3E5', borderRadius: '18px', padding: '22px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                <span style={{ fontSize: '1.4rem' }}></span>
                <h3 style={{ margin: 0, color: '#15253F', fontSize: '1.08rem', fontWeight: 900 }}>Hierarchy &amp; Approval Workflows</h3>
              </div>
              <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '0.88rem', color: '#44474D', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <li>Interactive organization tree (Organogram).</li>
                <li>Multi-level leave &amp; attendance regularization approvals.</li>
                <li>Delegated team management &amp; shift scheduling.</li>
              </ul>
            </div>
          </div>

          {/* Pricing Overview */}
          <div style={{ marginBottom: '32px' }}>
            <h2 style={{ color: '#15253F', fontSize: '1.25rem', fontWeight: 900, marginBottom: '14px', borderBottom: '2px solid #EBEEF0', paddingBottom: '8px' }}>
              Transparent Subscription Tiers
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
              <div style={{ background: '#F7FAFC', border: '1px solid #E5E9EB', padding: '18px', borderRadius: '14px', textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 4px', color: '#181C1E', fontSize: '1rem', fontWeight: 800 }}>Basic Plan</h4>
                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#15253F' }}>₹29 <span style={{ fontSize: '0.8rem', color: '#6B7280', fontWeight: 600 }}>/emp/mo</span></div>
                <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#6B7280' }}>Core Biometric Attendance &amp; Geofencing</p>
              </div>

              <div style={{ background: '#15253F', border: '1px solid #000F29', padding: '18px', borderRadius: '14px', textAlign: 'center', color: '#FFF' }}>
                <span style={{ background: '#C5D8FD', color: '#15253F', fontSize: '0.65rem', fontWeight: 900, padding: '2px 8px', borderRadius: '99px', textTransform: 'uppercase' }}>Most Popular</span>
                <h4 style={{ margin: '4px 0 4px', color: '#FFF', fontSize: '1rem', fontWeight: 800 }}>Pro Plan</h4>
                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#C5D8FD' }}>₹49 <span style={{ fontSize: '0.8rem', color: '#C5D8FD', fontWeight: 600 }}>/emp/mo</span></div>
                <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#C5D8FD' }}>Full HRMS + Automated Payroll + Organogram</p>
              </div>

              <div style={{ background: '#F7FAFC', border: '1px solid #E5E9EB', padding: '18px', borderRadius: '14px', textAlign: 'center' }}>
                <h4 style={{ margin: '0 0 4px', color: '#181C1E', fontSize: '1rem', fontWeight: 800 }}>Enterprise</h4>
                <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#15253F' }}>Custom</div>
                <p style={{ margin: '6px 0 0', fontSize: '0.78rem', color: '#6B7280' }}>Dedicated SLA + Custom API Integrations</p>
              </div>
            </div>
          </div>

          {/* Corporate Entity Footer Block */}
          <div style={{ borderTop: '2px dashed #E5E9EB', paddingTop: '24px', background: '#F1F4F6', padding: '22px', borderRadius: '18px', border: '1.5px solid #E0E3E5' }}>
            <h3 style={{ color: '#15253F', fontSize: '1.05rem', fontWeight: 900, marginBottom: '8px' }}> Corporate Entity &amp; Registered Office</h3>
            <p style={{ margin: 0, fontSize: '0.88rem', color: '#44474D', lineHeight: 1.6 }}>
              <strong>Bharat Expert Gen Technologies Pvt. Ltd</strong><br />
              <strong>Registered Office:</strong> 1/93, Vijay Khand, Ujariyaon, Vijay Khand 2, Gomti Nagar, Lucknow, Uttar Pradesh 226010<br />
              <strong>Call Us:</strong> <a href="tel:+917380325516" style={{ color: '#15253F', fontWeight: 800 }}>+91 7380325516</a> |
              <strong> Email:</strong> <a href="mailto:smarthomes467@gmail.com" style={{ color: '#15253F', fontWeight: 800 }}>smarthomes467@gmail.com</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ========== Book Demo Modal ========== */
function BookDemoModal({ onClose }) {
  const [demoForm, setDemoForm] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    teamSize: '21-100 Staff',
    preferredDate: '',
    preferredChannel: 'Video Call (Google Meet / Zoom)',
    notes: '',
  })
  const [loading, setLoading] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setSuccessMsg('')
    setErrorMsg('')
    try {
      const res = await fetch('/api/book-demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...demoForm,
          requestedAt: new Date().toISOString(),
        })
      })
      const data = await res.json()
      if (res.ok && data.success) {
        setSuccessMsg(data.message || 'Demo Booked Successfully! Our HR Solutions Specialist will contact you within 2 hours to confirm your live session.')
      } else {
        setErrorMsg(data.message || 'Failed to book demo. Please check your inputs and try again.')
      }
    } catch (err) {
      console.error('Demo request error:', err)
      setErrorMsg('Network error. Please call +91 7380325516 or try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.7)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', backdropFilter: 'blur(5px)' }}>
      <div style={{ background: '#FFFFFF', borderRadius: '24px', width: '100%', maxWidth: '540px', padding: '30px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.3)', position: 'relative', border: '1px solid #E5E9EB' }}>
        <button
          onClick={onClose}
          style={{ position: 'absolute', right: '20px', top: '20px', background: '#F1F4F6', border: 'none', borderRadius: '50%', width: '36px', height: '36px', fontSize: '1.1rem', cursor: 'pointer', color: '#6B7280', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}
        >
          <CrossIcon size={18} />
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '22px' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: '#E8EEFF', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1.5px solid #C5D8FD', color: '#355DD0' }}>
            <CalendarIcon size={26} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 900, color: '#08111C' }}>Book a Live Product Demo</h3>
            <span style={{ fontSize: '0.82rem', color: '#6B7280', fontWeight: 600 }}>Get a personalized walkthrough with an Enterprise HR Specialist</span>
          </div>
        </div>

        {successMsg ? (
          <div style={{ background: '#F1F4F6', border: '1.5px solid #C5D8FD', borderRadius: '20px', padding: '28px 20px', textAlign: 'center', margin: '15px 0' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 56, height: 56, borderRadius: '50%', background: '#DCFCE7', color: '#16A34A', margin: '0 auto 14px' }}>
              <CheckCircleIcon size={32} color="#16A34A" />
            </div>
            <h4 style={{ margin: '0 0 10px', fontSize: '1.18rem', fontWeight: 900, color: '#15253F' }}>Live Demo Request Confirmed!</h4>
            <p style={{ margin: 0, fontSize: '0.88rem', color: '#15253F', lineHeight: 1.6, fontWeight: 600 }}>{successMsg}</p>
            <button
              onClick={onClose}
              style={{ marginTop: '22px', width: '100%', background: '#4D5F7F', color: '#FFF', border: 'none', padding: '12px', borderRadius: '14px', fontWeight: 800, fontSize: '0.94rem', cursor: 'pointer' }}
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {errorMsg && (
              <div style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', padding: '10px 14px', borderRadius: '12px', fontSize: '0.82rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <AlertTriangleIcon size={16} color="#DC2626" />
                <span>{errorMsg}</span>
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.74rem', fontWeight: 800, color: '#44474D', display: 'block', marginBottom: '4px', letterSpacing: '0.4px' }}>FULL NAME *</label>
                <input
                  type="text"
                  required
                  placeholder="Prince Singh"
                  value={demoForm.name}
                  onChange={(e) => setDemoForm({ ...demoForm, name: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #E0E3E5', fontSize: '0.88rem', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.74rem', fontWeight: 800, color: '#44474D', display: 'block', marginBottom: '4px', letterSpacing: '0.4px' }}>COMPANY NAME *</label>
                <input
                  type="text"
                  required
                  placeholder="Bharat Expert Gen Technologies Pvt. Ltd"
                  value={demoForm.companyName}
                  onChange={(e) => setDemoForm({ ...demoForm, companyName: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #E0E3E5', fontSize: '0.88rem', outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.74rem', fontWeight: 800, color: '#44474D', display: 'block', marginBottom: '4px', letterSpacing: '0.4px' }}>WORK EMAIL *</label>
                <input
                  type="email"
                  required
                  placeholder="admin@company.com"
                  value={demoForm.email}
                  onChange={(e) => setDemoForm({ ...demoForm, email: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #E0E3E5', fontSize: '0.88rem', outline: 'none' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '0.74rem', fontWeight: 800, color: '#44474D', display: 'block', marginBottom: '4px', letterSpacing: '0.4px' }}>PHONE NUMBER *</label>
                <input
                  type="tel"
                  required
                  placeholder="+91 98765 43210"
                  value={demoForm.phone}
                  onChange={(e) => setDemoForm({ ...demoForm, phone: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #E0E3E5', fontSize: '0.88rem', outline: 'none' }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.74rem', fontWeight: 800, color: '#44474D', display: 'block', marginBottom: '4px', letterSpacing: '0.4px' }}>TEAM SIZE</label>
                <select
                  value={demoForm.teamSize}
                  onChange={(e) => setDemoForm({ ...demoForm, teamSize: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #E0E3E5', fontSize: '0.88rem', background: '#FFF', outline: 'none' }}
                >
                  <option value="1-20 Staff">1-20 Staff</option>
                  <option value="21-100 Staff">21-100 Staff</option>
                  <option value="101-500 Staff">101-500 Staff</option>
                  <option value="500+ Enterprise">500+ Enterprise</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: '0.74rem', fontWeight: 800, color: '#44474D', display: 'block', marginBottom: '4px', letterSpacing: '0.4px' }}>DEMO MODE</label>
                <select
                  value={demoForm.preferredChannel}
                  onChange={(e) => setDemoForm({ ...demoForm, preferredChannel: e.target.value })}
                  style={{ width: '100%', padding: '10px 14px', borderRadius: '12px', border: '1.5px solid #E0E3E5', fontSize: '0.88rem', background: '#FFF', outline: 'none' }}
                >
                  <option value="Video Call (Google Meet / Zoom)">Video Call (Google Meet / Zoom)</option>
                  <option value="In-Person Office Visit">In-Person Office Visit</option>
                  <option value="Phone Call">Phone Call</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                marginTop: '10px',
                width: '100%',
                background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)',
                color: '#FFFFFF',
                border: 'none',
                padding: '14px',
                borderRadius: '14px',
                fontWeight: 900,
                fontSize: '0.96rem',
                cursor: 'pointer',
                boxShadow: '0 8px 20px rgba(0, 15, 41, 0.25)'
              }}
            >
              {loading ? 'Submitting Request...' : ' Confirm Live Demo Booking →'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

/* ========== FULL PAGE 1: PRIVACY POLICY PAGE (/#privacy) ========== */
function PrivacyPolicyPage({ onGoHome }) {
  return (
    <div style={{ background: '#F7FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', color: '#FFFFFF', padding: '30px 0', boxShadow: '0 4px 20px rgba(0, 15, 41, 0.15)' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <a href="/" onClick={(e) => { e.preventDefault(); onGoHome(); }} className="nav__logo" style={{ color: '#FFF' }}>
            <span className="nav__logo-icon"><LogoMark size={28} /></span>
            Hr<span className="nav__logo-accent" style={{ color: '#C5D8FD' }}>PocketPRO</span>
          </a>
          <button
            onClick={onGoHome}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#FFF', border: '1px solid rgba(255,255,255,0.3)', padding: '10px 20px', borderRadius: '12px', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            ← Return to Homepage
          </button>
        </div>
        <div className="container" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', border: '1px solid rgba(255,255,255,0.2)' }}>
              <ShieldCheckIcon size={28} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, color: '#FFF' }}>Privacy Policy</h1>
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#C5D8FD', fontWeight: 600 }}>hrpocketPRO Data Protection &amp; Biometric Security Commitment • Effective August 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container" style={{ padding: '40px 20px', flex: 1, maxWidth: '900px' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '24px', padding: '36px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #E5E9EB', display: 'flex', flexDirection: 'column', gap: '24px', color: '#44474D', lineHeight: 1.75 }}>

          <div style={{ background: '#F1F4F6', borderLeft: '5px solid #15253F', padding: '16px 20px', borderRadius: '12px', border: '1.5px solid #E0E3E5', borderLeftWidth: '5px' }}>
            <h4 style={{ margin: '0 0 6px', color: '#15253F', fontSize: '1.02rem', fontWeight: 900 }}> Executive Privacy Promise</h4>
            <p style={{ margin: 0, fontSize: '0.9rem', color: '#44474D', fontWeight: 600 }}>
              hrpocketPRO values the trust placed in us by companies and employees. We enforce strict zero-selling data protection standards, end-to-end cloud encryption, and non-reversible biometric vectors.
            </p>
          </div>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>1. Information We Collect</h3>
            <p style={{ margin: 0 }}>
              hrpocketPRO collects data exclusively required for biometric authentication, location-verified attendance, and automated payroll computations:
            </p>
            <ul style={{ paddingLeft: '20px', marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <li><strong>Biometric Representations:</strong> 192-dimensional mathematical feature vectors generated from live selfie scans.</li>
              <li><strong>Location Data:</strong> Precise GPS coordinates captured strictly during clock-in and clock-out attempts to verify geofence radius compliance.</li>
              <li><strong>Profile Metadata:</strong> Employee name, company email, designation, CTC breakdown, employee ID, and assigned office branch.</li>
              <li><strong>Device Telemetry:</strong> Device model, OS version, and app version to detect mock GPS applications and security bypasses.</li>
            </ul>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>2. Biometric Data Protection &amp; Non-Reversibility</h3>
            <p style={{ margin: 0 }}>
              Raw facial photographs captured during check-ins are processed in temporary volatile memory and discarded immediately upon feature extraction. We <strong>never store raw photographs</strong> on persistent storage. Biometric templates are stored as non-reversible floating-point mathematical matrices from which human faces cannot be reconstructed or reverse-engineered.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>3. Data Storage &amp; Encryption Infrastructure</h3>
            <p style={{ margin: 0 }}>
              All database records and network traffic are secured using enterprise security protocols:
            </p>
            <ul style={{ paddingLeft: '20px', marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <li><strong>Data in Transit:</strong> Encrypted using TLS 1.3 cryptographic protocols with mandatory HTTPS.</li>
              <li><strong>Data at Rest:</strong> Encrypted using AES-256 bit encryption within MongoDB Atlas &amp; Firebase Enterprise clusters.</li>
              <li><strong>Access Control:</strong> Granular Role-Based Access Control (RBAC) ensuring data access is limited to authorized HR Admins.</li>
            </ul>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>4. Zero Third-Party Selling</h3>
            <p style={{ margin: 0 }}>
              We strictly comply with DPDP Act 2023 (India) and GDPR principles. hrpocketPRO does not monetize, license, sell, or rent personal data or attendance logs to third-party ad networks, data brokers, or external entities.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>5. User Rights &amp; Contact</h3>
            <p style={{ margin: '0 0 14px' }}>
              Employees or corporate administrators seeking data export files or requesting permanent biometric vector erasure upon employment termination can submit a formal request to <a href="mailto:smarthomes467@gmail.com" style={{ color: '#15253F', fontWeight: 800 }}>smarthomes467@gmail.com</a>.
            </p>
          </section>

          <section style={{ borderTop: '1px solid #E5E9EB', paddingTop: '20px', background: '#F1F4F6', padding: '20px', borderRadius: '16px', border: '1px solid #E0E3E5' }}>
            <h3 style={{ color: '#15253F', fontSize: '1.05rem', fontWeight: 900, marginBottom: '8px' }}> Corporate Entity &amp; Registered Office</h3>
            <p style={{ margin: 0, fontSize: '0.88rem', color: '#44474D', lineHeight: 1.6 }}>
              <strong>Bharat Expert Gen Technologies Pvt. Ltd</strong><br />
              <strong>Registered Office:</strong> 1/93, Vijay Khand, Ujariyaon, Vijay Khand 2, Gomti Nagar, Lucknow, Uttar Pradesh 226010<br />
              <strong>Call Us:</strong> <a href="tel:+917380325516" style={{ color: '#15253F', fontWeight: 800 }}>+91 7380325516</a><br />
              <strong>Email:</strong> <a href="mailto:smarthomes467@gmail.com" style={{ color: '#15253F', fontWeight: 800 }}>smarthomes467@gmail.com</a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

/* ========== FULL PAGE 2: TERMS & CONDITIONS PAGE (/#terms) ========== */
function TermsConditionsPage({ onGoHome }) {
  return (
    <div style={{ background: '#F7FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', color: '#FFFFFF', padding: '30px 0', boxShadow: '0 4px 20px rgba(0, 15, 41, 0.15)' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <a href="/" onClick={(e) => { e.preventDefault(); onGoHome(); }} className="nav__logo" style={{ color: '#FFF' }}>
            <span className="nav__logo-icon"><LogoMark size={28} /></span>
            Hr<span className="nav__logo-accent" style={{ color: '#C5D8FD' }}>PocketPRO</span>
          </a>
          <button
            onClick={onGoHome}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#FFF', border: '1px solid rgba(255,255,255,0.3)', padding: '10px 20px', borderRadius: '12px', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            ← Return to Homepage
          </button>
        </div>
        <div className="container" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', border: '1px solid rgba(255,255,255,0.2)' }}>
              <DocumentTextIcon size={28} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, color: '#FFF' }}>Terms &amp; Conditions</h1>
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#C5D8FD', fontWeight: 600 }}>Master SaaS Agreement &amp; Acceptable Usage Policy • Last Modified August 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container" style={{ padding: '40px 20px', flex: 1, maxWidth: '900px' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '24px', padding: '36px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #E5E9EB', display: 'flex', flexDirection: 'column', gap: '24px', color: '#44474D', lineHeight: 1.75 }}>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>1. SaaS Master Agreement</h3>
            <p style={{ margin: 0 }}>
              These Terms &amp; Conditions govern your corporate subscription and access to the hrpocketPRO Attendance, Payroll, and HRMS platform. By creating a Company Account, executing a order form, or logging in, you agree to these terms on behalf of your organization.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>2. Anti-Fraud &amp; Geofence Enforcement</h3>
            <p style={{ margin: 0 }}>
              hrpocketPRO provides strict location-verified attendance. Users are prohibited from utilizing software modification tools, mock GPS location applications, VPN tunnels, or emulator environments to fake check-in events. hrpocketPRO security filters automatically flag and record tamper events for administrative audit.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>3. Service Level Agreement (99.9% Uptime)</h3>
            <p style={{ margin: 0 }}>
              hrpocketPRO guarantees 99.9% availability of core API endpoints for attendance clock-ins. Scheduled maintenance windows are performed during low-traffic weekend hours with prior notification sent to SuperAdmins.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>4. Intellectual Property</h3>
            <p style={{ margin: 0 }}>
              hrpocketPRO retains all rights, title, and interest in and to the mobile apps, web dashboard, proprietary face-matching AI models, trademarks, logos, and underlying source code.
            </p>
          </section>

          <section style={{ borderTop: '1px solid #E5E9EB', paddingTop: '20px', background: '#F1F4F6', padding: '20px', borderRadius: '16px', border: '1px solid #E0E3E5' }}>
            <h3 style={{ color: '#15253F', fontSize: '1.05rem', fontWeight: 900, marginBottom: '8px' }}> Corporate Entity &amp; Registered Office</h3>
            <p style={{ margin: 0, fontSize: '0.88rem', color: '#44474D', lineHeight: 1.6 }}>
              <strong>Bharat Expert Gen Technologies Pvt. Ltd</strong><br />
              <strong>Registered Office:</strong> 1/93, Vijay Khand, Ujariyaon, Vijay Khand 2, Gomti Nagar, Lucknow, Uttar Pradesh 226010<br />
              <strong>Call Us:</strong> <a href="tel:+917380325516" style={{ color: '#15253F', fontWeight: 800 }}>+91 7380325516</a><br />
              <strong>Email:</strong> <a href="mailto:smarthomes467@gmail.com" style={{ color: '#15253F', fontWeight: 800 }}>smarthomes467@gmail.com</a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}

/* ========== FULL PAGE 3: REFUND & CANCELLATION POLICY PAGE (/#refund) ========== */
function RefundPolicyPage({ onGoHome }) {
  return (
    <div style={{ background: '#F7FAFC', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <header style={{ background: 'linear-gradient(135deg, #15253F 0%, #000F29 100%)', color: '#FFFFFF', padding: '30px 0', boxShadow: '0 4px 20px rgba(0, 15, 41, 0.15)' }}>
        <div className="container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <a href="/" onClick={(e) => { e.preventDefault(); onGoHome(); }} className="nav__logo" style={{ color: '#FFF' }}>
            <span className="nav__logo-icon"><LogoMark size={28} /></span>
            Hr<span className="nav__logo-accent" style={{ color: '#C5D8FD' }}>PocketPRO</span>
          </a>
          <button
            onClick={onGoHome}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#FFF', border: '1px solid rgba(255,255,255,0.3)', padding: '10px 20px', borderRadius: '12px', fontWeight: 800, fontSize: '0.88rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            ← Return to Homepage
          </button>
        </div>
        <div className="container" style={{ marginTop: '30px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '18px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.8rem', border: '1px solid rgba(255,255,255,0.2)' }}>
              <ReceiptRefundIcon size={28} />
            </div>
            <div>
              <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, color: '#FFF' }}>Refund &amp; Cancellation Policy</h1>
              <p style={{ margin: '4px 0 0', fontSize: '0.9rem', color: '#C5D8FD', fontWeight: 600 }}>Transparent Subscription Billing &amp; Refund Guarantee • Updated August 2026</p>
            </div>
          </div>
        </div>
      </header>

      <main className="container" style={{ padding: '40px 20px', flex: 1, maxWidth: '900px' }}>
        <div style={{ background: '#FFFFFF', borderRadius: '24px', padding: '36px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', border: '1px solid #E5E9EB', display: 'flex', flexDirection: 'column', gap: '24px', color: '#44474D', lineHeight: 1.75 }}>

          <div style={{ background: '#F1F4F6', borderLeft: '5px solid #4D5F7F', padding: '18px 22px', borderRadius: '14px', border: '1.5px solid #C5D8FD', borderLeftWidth: '5px' }}>
            <h4 style={{ margin: '0 0 6px', color: '#15253F', fontSize: '1.08rem', fontWeight: 900 }}> 7-Day Unconditional Money-Back Guarantee</h4>
            <p style={{ margin: 0, fontSize: '0.92rem', color: '#15253F', fontWeight: 600 }}>
              Evaluate hrpocketPRO completely risk-free. If you choose to cancel your subscription within 7 days of initial signup, we will issue a 100% full refund with no questions asked.
            </p>
          </div>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>1. Subscription Billing Cycle</h3>
            <p style={{ margin: 0 }}>
              hrpocketPRO plans are billed on a recurring monthly or annual basis depending on your selected billing tier (Basic, Pro, or Enterprise). Invoices are automatically generated and sent to your registered company billing email address.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>2. Subscription Cancellation</h3>
            <p style={{ margin: 0 }}>
              You may cancel your corporate subscription at any time directly from the SuperAdmin Account &amp; Billing settings portal or by emailing <a href="mailto:smarthomes467@gmail.com" style={{ color: '#15253F', fontWeight: 800 }}>smarthomes467@gmail.com</a>. Upon cancellation, your account will remain fully functional until the end of your current paid billing period.
            </p>
          </section>

          <section>
            <h3 style={{ color: '#15253F', fontSize: '1.2rem', fontWeight: 900, marginBottom: '10px' }}>3. Refund Processing &amp; Timelines</h3>
            <p style={{ margin: 0 }}>
              Eligible refunds requested within the 7-day guarantee window are credited back to the original payment instrument (Credit Card, Net Banking, or UPI) within <strong>3 to 5 business days</strong>.
            </p>
          </section>

          <section style={{ borderTop: '1px solid #E5E9EB', paddingTop: '20px', background: '#F1F4F6', padding: '20px', borderRadius: '16px', border: '1px solid #E0E3E5' }}>
            <h3 style={{ color: '#15253F', fontSize: '1.05rem', fontWeight: 900, marginBottom: '8px' }}> Corporate Entity &amp; Registered Office</h3>
            <p style={{ margin: 0, fontSize: '0.88rem', color: '#44474D', lineHeight: 1.6 }}>
              <strong>Bharat Expert Gen Technologies Pvt. Ltd</strong><br />
              <strong>Registered Office:</strong> 1/93, Vijay Khand, Ujariyaon, Vijay Khand 2, Gomti Nagar, Lucknow, Uttar Pradesh 226010<br />
              <strong>Call Us:</strong> <a href="tel:+917380325516" style={{ color: '#15253F', fontWeight: 800 }}>+91 7380325516</a><br />
              <strong>Email:</strong> <a href="mailto:smarthomes467@gmail.com" style={{ color: '#15253F', fontWeight: 800 }}>smarthomes467@gmail.com</a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  )
}
